/**
 * Captures local credentials without persisting them in browser storage.
 */

import { useEffect, useState, type SyntheticEvent } from "react";

import type { InternalCaseApiClient } from "@libro-reclamos/api-client/InternalCases";

import { ActionButton } from "../atoms/ActionButton";

interface LoginPanelProps {
  /**
   * Base del API, **con su prefijo de versión ya incluido** (`PUBLIC_API_BASE_URL`, del estilo
   * `https://host/api/v1`). Hace falta explícita por dos razones: el backoffice y el API viven en
   * hosts distintos, así que una ruta relativa apuntaría al front, donde no existe; y el prefijo
   * viene en la base, así que volver a escribirlo aquí produciría `/api/v1/api/v1/...`, que es
   * exactamente el 404 que costó encontrar.
   */
  readonly apiBaseUrl: string;
  readonly client: InternalCaseApiClient;
  readonly onAuthenticated: () => void;
  readonly onError: (message: string) => void;
}

/**
 * Starts a local session; the refresh credential remains in an HttpOnly cookie.
 */
export function LoginPanel({ apiBaseUrl, client, onAuthenticated, onError }: LoginPanelProps) {
  // Whether the Continnua core is available for signing in. Asked once, and any failure — the 404
  // the API returns when the core is switched off included — leaves this false, so the screen
  // shows only the local form, exactly as before this phase
  // (`continnua_plataforma/docs/FASE-3.md` §8).
  const [platformAccessEnabled, setPlatformAccessEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void client.platformAccessEnabled().then((enabled) => {
      if (!cancelled) {
        setPlatformAccessEnabled(enabled);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [client]);

  async function Submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    const fields = new FormData(event.currentTarget);
    try {
      const companySlug = fields.get("companySlug");
      const email = fields.get("email");
      const password = fields.get("password");
      // Un operador de plataforma no pertenece a ninguna empresa: el contrato
      // espera que el campo se omita, no que viaje vacío.
      const slug = typeof companySlug === "string" ? companySlug.trim() : "";
      await client.login({
        ...(slug === "" ? {} : { companySlug: slug }),
        email: typeof email === "string" ? email : "",
        password: typeof password === "string" ? password : "",
      });
      onAuthenticated();
    } catch {
      onError("No fue posible iniciar sesión. Verifica la empresa y tus credenciales.");
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-[var(--color-surface-canvas)] px-6 py-12">
      <section className="w-full max-w-md rounded-[var(--radius-medium)] border border-[var(--color-border-default)] bg-white p-8 shadow-sm">
        <p className="text-sm font-semibold text-[var(--color-brand-primary)]">Operación interna</p>
        <h1 className="mt-2 text-2xl font-bold text-[var(--color-brand-deep)]">
          Gestión del Libro de Reclamos
        </h1>
        <p className="mt-3 text-sm text-[var(--color-text-muted)]">
          Ingresa con la compañía que deseas administrar. Los operadores de plataforma dejan la
          empresa en blanco.
        </p>
        <form className="mt-8 grid gap-5" onSubmit={(event) => void Submit(event)}>
          <label className="grid gap-2 text-sm font-semibold">
            Empresa
            <input
              autoComplete="organization"
              className="rounded-[var(--radius-small)] border border-[var(--color-border-default)] px-3 py-2 font-normal"
              name="companySlug"
              placeholder="Vacío para operadores de plataforma"
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold">
            Correo
            <input
              autoComplete="username"
              className="rounded-[var(--radius-small)] border border-[var(--color-border-default)] px-3 py-2 font-normal"
              name="email"
              required
              type="email"
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold">
            Contraseña
            <input
              autoComplete="current-password"
              className="rounded-[var(--radius-small)] border border-[var(--color-border-default)] px-3 py-2 font-normal"
              minLength={8}
              name="password"
              required
              type="password"
            />
          </label>
          <ActionButton className="mt-2 w-full" type="submit">
            Iniciar sesión
          </ActionButton>
        </form>
        {platformAccessEnabled ? (
          <div className="mt-6 border-t border-[var(--color-border-default)] pt-6">
            <p className="text-sm text-[var(--color-text-muted)]">
              Si tu cuenta ya está en Continnua, entra desde ahí y no necesitas contraseña de este
              módulo.
            </p>
            {/*
              A full-page navigation, not a fetch: it is the browser itself that has to follow the
              redirect to the core and, later, come back to the callback. A `fetch` would lose the
              transit cookie that carries the state, the nonce and the PKCE verifier.

              Y absoluta contra la base del API, no relativa: el backoffice se sirve en otro host,
              donde esta ruta no existe. La base ya trae el prefijo de versión, así que aquí solo va
              lo que sigue — igual que las rutas que usa el cliente HTTP.
            */}
            <a
              className="mt-4 grid w-full place-items-center rounded-[var(--radius-small)] border border-[var(--color-brand-primary)] px-3 py-2 text-sm font-semibold text-[var(--color-brand-primary)]"
              href={`${apiBaseUrl.replace(/\/$/u, "")}/auth/platform/start`}
            >
              Entrar con Continnua
            </a>
          </div>
        ) : null}
      </section>
    </main>
  );
}
